import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  Chip,
  Box,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { decode } from "html-entities";
import { TJsonNoteWithFlag } from "../../form/types";

interface SubscriptionNotesEditorProps {
  notes: TJsonNoteWithFlag[];
  currentUserId: number;
  onAdd: (note: string) => void;
  onDelete: (index: number) => void;
  requiresNote?: boolean;
  hasChanges?: boolean;
  changedFields?: string[];
}

const renderSimpleHtml = (html: string) => {
  const decoded = decode(html);

  return decoded
    .split(/<\/?p>/gi)
    .filter(Boolean)
    .map((block, i) => {
      const lines = block.split(/<br\s*\/?>/gi);
      return (
        <p key={i}>
          {lines.map((line, j) => (
            <React.Fragment key={`${i}-${j}`}>
              {line}
              {j < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      );
    });
};

/**
 * SubscriptionNotesEditor displays and manages a list of notes for a subscription.
 *
 * - Shows the most recent note by default, with an option to expand and view all older notes.
 * - Allows the current user to add a new note or delete their own notes.
 * - Notes should be passed in sorted order (most recent first) for correct display.
 *
 * @component
 * @param {Object} props
 * @param {TJsonNoteWithFlag[]} props.notes - Array of note objects. Must be sorted by timestamp descending (most recent first - newly created notes are always on top).
 * @param {number} props.currentUserId - The ID of the current user, used to determine delete permissions.
 * @param {(note: string) => void} props.onAdd - Callback when a new note is added.
 * @param {(index: number) => void} props.onDelete - Callback when a note is deleted, receives the index of the note in the original array.
 */
export const SubscriptionNotesEditor: React.FC<SubscriptionNotesEditorProps> = ({
  notes,
  currentUserId,
  onAdd,
  onDelete,
  requiresNote = false,
  hasChanges = false,
  changedFields = [],
}) => {
  const [newNote, setNewNote] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    onAdd(newNote.trim());
    setNewNote("");
    setIsAdding(false);
  };

  const latestNote = notes[0];
  const olderNotes = notes.slice(1);

  return (
    <Paper sx={{
      p: 2,
      border: notes.length === 0 ? "1px solid red" : requiresNote ? "2px solid #ff9800" : "none",
      backgroundColor: requiresNote ? "#fff3e0" : "white",
    }}>
      {/* Note requirement alert */}
      {requiresNote && hasChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Note Required:</strong> You have made changes that require a note before saving.
            {changedFields.length > 0 && (
              <> Changed: {changedFields.join(', ')}</>
            )}
          </Typography>
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Notes</Typography>
          {requiresNote && (
            <Chip
              label="Required"
              color="warning"
              size="small"
              sx={{ animation: 'pulse 2s ease-in-out infinite' }}
            />
          )}
        </Box>
        {!isAdding ? (
          <Button
            variant={requiresNote ? "contained" : "outlined"}
            color={requiresNote ? "warning" : "primary"}
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setIsAdding(true)}
            sx={requiresNote ? {
              animation: 'pulse 2s ease-in-out infinite',
              fontWeight: 600,
            } : {}}
          >
            {requiresNote ? "Add Required Note" : "Add Note"}
          </Button>
        ) : (
          <Button
            variant="text"
            size="small"
            color="secondary"
            onClick={() => {
              setIsAdding(false);
              setNewNote("");
            }}
          >
            Cancel
          </Button>
        )}
      </Stack>

      {isAdding && (
        <>
          <TextField
            label="New Note"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            margin="normal"
          />
          <Button
            variant="contained"
            onClick={handleAddNote}
            disabled={!newNote.trim()}
            sx={{ mb: 2 }}
          >
            Add Note
          </Button>
        </>
      )}

      {notes.length === 0 && (
        <Typography color="textSecondary">No notes available</Typography>
      )}

      {latestNote && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: "#f5f5f5",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography fontWeight="bold">
              {latestNote.created_user_name} · {latestNote.timestamp}
            </Typography>
            {(latestNote.localOnly && latestNote.created_user_id === currentUserId) && (
              <IconButton
                size="small"
                onClick={() => onDelete(0)}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>
          <Typography variant="body2" component="div" mt={1}>
            {renderSimpleHtml(latestNote.note)}
          </Typography>
        </Paper>
      )}

      {olderNotes.length > 0 && (
        <Accordion
          expanded={showAll}
          onChange={() => setShowAll(!showAll)}
          TransitionProps={{ timeout: 150 }}
          sx={{
            backgroundColor: showAll ? "#f0f4ff" : "transparent",
            mt: 2,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Show {showAll ? "Less" : "All"} Notes</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {olderNotes.map((note, i) => (
              <Paper
                key={i}
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography fontWeight="bold">
                    {note.created_user_name} · {note.timestamp}
                  </Typography>
                  {(note.localOnly && note.created_user_id === currentUserId) && (
                    <IconButton
                      size="small"
                      onClick={() => onDelete(i + 1)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>
                <Typography variant="body2" component="div" mt={1}>
                  {renderSimpleHtml(note.note)}
                </Typography>
              </Paper>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
};
